import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createBanner, updateBanner, deleteBanner, fetchCarouselBanners, fetchLoginBanner, clearError, clearSuccess } from '../redux/slices/bannerSlice';
import LottiePlayer from '../Components/LottiePlayer';

const BannerAdmin = () => {
    const dispatch = useDispatch();
    const { carouselBanners, loginBanner, loading, error, success } = useSelector((state) => state.banner);
    const [formData, setFormData] = useState({
        id: '',
        image: null,
        altText: '',
        bannerType: 'carousel',
        isActive: true,
    });
    const [preview, setPreview] = useState(null);
    const [formError, setFormError] = useState('');

    useEffect(() => {
        dispatch(fetchCarouselBanners());
        dispatch(fetchLoginBanner());
        if (success) {
            setFormData({ id: '', image: null, altText: '', bannerType: 'carousel', isActive: true });
            setPreview(null);
            setTimeout(() => dispatch(clearSuccess()), 3000);
        }
        if (error) {
            setTimeout(() => dispatch(clearError()), 3000);
        }
    }, [dispatch, success, error]);

    const handleInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        setFormError('');
        if (type === 'file') {
            const file = files[0];
            if (file && !file.type.startsWith('image/')) {
                setFormError('Please upload an image file');
                return;
            }
            setFormData({ ...formData, image: file });
            setPreview(file ? URL.createObjectURL(file) : null);
        } else if (type === 'checkbox') {
            setFormData({ ...formData, [name]: checked });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.altText.trim()) {
            setFormError('Alt text is required');
            return;
        }
        if (!formData.id && !formData.image) {
            setFormError('Image is required for new banners');
            return;
        }

        const data = new FormData();
        if (formData.image) data.append('image', formData.image);
        data.append('altText', formData.altText);
        data.append('bannerType', formData.bannerType);
        data.append('isActive', formData.isActive);

        if (formData.id) {
            dispatch(updateBanner({ id: formData.id, formData: data }));
        } else {
            dispatch(createBanner(data));
        }
    };

    const handleEdit = (banner) => {
        setFormData({
            id: banner._id,
            image: null,
            altText: banner.altText,
            bannerType: banner.bannerType,
            isActive: banner.isActive,
        });
        setPreview(banner.imageUrl);
        setFormError('');
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this banner?')) {
            dispatch(deleteBanner(id));
        }
    };

    return (
        <div className="p-6 max-w-4xl mx-auto bg-custom-white min-h-screen">
            <h2 className="text-2xl font-bold text-custom-blue mb-6">Manage Banners</h2>
            {success && (
                <div className="mb-4 p-3 bg-custom-yellow text-custom-blue rounded-lg shadow">
                    {success}
                </div>
            )}
            {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg shadow">
                    {error}
                </div>
            )}
            {formError && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg shadow">
                    {formError}
                </div>
            )}

            {/* Create/Edit Form */}
            <form onSubmit={handleSubmit} className="mb-8 p-6 bg-custom-white rounded-lg shadow-lg">
                <div className="mb-4">
                    <label className="block text-custom-blue font-medium mb-1">Image</label>
                    <input
                        type="file"
                        name="image"
                        accept="image/*"
                        onChange={handleInputChange}
                        className="w-full p-2 border border-gray-300 rounded text-custom-blue"
                    />
                    {preview && (
                        <img
                            src={preview}
                            alt="Preview"
                            className="mt-2 w-32 h-32 object-cover rounded border border-gray-300"
                        />
                    )}
                </div>
                <div className="mb-4">
                    <label className="block text-custom-blue font-medium mb-1">Alt Text</label>
                    <input
                        type="text"
                        name="altText"
                        value={formData.altText}
                        onChange={handleInputChange}
                        required
                        className="w-full p-2 border border-gray-300 rounded text-custom-blue focus:ring-2 focus:ring-custom-yellow"
                    />
                </div>
                <div className="mb-4">
                    <label className="block text-custom-blue font-medium mb-1">Banner Type</label>
                    <select
                        name="bannerType"
                        value={formData.bannerType}
                        onChange={handleInputChange}
                        className="w-full p-2 border border-gray-300 rounded text-custom-blue focus:ring-2 focus:ring-custom-yellow"
                    >
                        <option value="carousel">Carousel</option>
                        <option value="login">Login</option>
                    </select>
                </div>
                <div className="mb-4">
                    <label className="flex items-center text-custom-blue font-medium">
                        <input
                            type="checkbox"
                            name="isActive"
                            checked={formData.isActive}
                            onChange={handleInputChange}
                            className="mr-2 h-4 w-4 text-custom-yellow focus:ring-custom-yellow border-gray-300 rounded"
                        />
                        Active
                    </label>
                </div>
                <button
                    type="submit"
                    disabled={loading}
                    className={`bg-custom-blue text-custom-white py-2 px-6 rounded-lg hover:bg-opacity-90 transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                >
                    {loading ? (

                        <LottiePlayer
                            src="https://lottie.host/b2b25f7e-1811-405a-9986-2d09791950ef/8cJ75x5MYO.lottie"
                            width={300}
                            height={300}
                        />

                    ) : formData.id ? (
                        'Update Banner'
                    ) : (
                        'Create Banner'
                    )}
                </button>
            </form>

            {/* Banner List */}
            <h3 className="text-xl font-bold text-custom-blue mb-4">Banners</h3>
            {loading && !carouselBanners.length && !loginBanner ? (
                <div className="flex items-center justify-center h-32">

                    <LottiePlayer
                        src="https://lottie.host/b2b25f7e-1811-405a-9986-2d09791950ef/8cJ75x5MYO.lottie"
                        width={300}
                        height={300}
                    />

                </div>
            ) : (
                <div className="grid gap-4">
                    {loginBanner && (
                        <div className="flex items-center p-4 bg-custom-white rounded-lg shadow-lg">
                            <img
                                src={loginBanner.imageUrl}
                                alt={loginBanner.altText}
                                className="w-24 h-24 object-cover rounded mr-4 border border-gray-300"
                            />
                            <div className="flex-1">
                                <p className="text-custom-blue font-medium">{loginBanner.altText}</p>
                                <p className="text-sm text-gray-500">
                                    Type: Login, Active: {loginBanner.isActive ? 'Yes' : 'No'}
                                </p>
                            </div>
                            <button
                                onClick={() => handleEdit(loginBanner)}
                                className="mr-4 text-custom-blue hover:text-custom-yellow font-medium"
                            >
                                Edit
                            </button>
                            <button
                                onClick={() => handleDelete(loginBanner._id)}
                                className="text-red-500 hover:text-red-700 font-medium"
                            >
                                Delete
                            </button>
                        </div>
                    )}
                    {carouselBanners.map((banner) => (
                        <div
                            key={banner._id}
                            className="flex items-center p-4 bg-custom-white rounded-lg shadow-lg"
                        >
                            <img
                                src={banner.imageUrl}
                                alt={banner.altText}
                                className="w-24 h-24 object-cover rounded mr-4 border border-gray-300"
                            />
                            <div className="flex-1">
                                <p className="text-custom-blue font-medium">{banner.altText}</p>
                                <p className="text-sm text-gray-500">
                                    Type: Carousel, Active: {banner.isActive ? 'Yes' : 'No'}
                                </p>
                            </div>
                            <button
                                onClick={() => handleEdit(banner)}
                                className="mr-4 text-custom-blue hover:text-custom-yellow font-medium"
                            >
                                Edit
                            </button>
                            <button
                                onClick={() => handleDelete(banner._id)}
                                className="text-red-500 hover:text-red-700 font-medium"
                            >
                                Delete
                            </button>
                        </div>
                    ))}
                    {!loginBanner && !carouselBanners.length && (
                        <p className="text-custom-blue text-center">No banners available</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default BannerAdmin;